/* eslint-disable no-console */

import { useState, useRef, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";

import UserChatBubble2 from "@/Components/Forms/FirmUserBtn2";

import avatarImg from "../../../assets/images/avatar.svg";
import { isRenderableChatMessage } from "../../../Utils/chatMessages.mjs";
import CallHeaderActions from "../../../features/calls/components/CallHeaderActions";
import { callsEnabled } from "../../../features/calls/callsConfig";
import { isIdle } from "../../../features/calls/callsModel.mjs";

import GroupMembers from "./GroupMembers";
import MessageList from "./MessageList";
import TypingIndicator from "./TypingIndicator";
import MessageInput from "./MessageInput";
import "./chatwindow.css";

export const formatName = (name = "") => {
  return name
    .trim()
    .split(" ")
    .filter((w) => w.length > 0)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

const ChatWindow = ({
  user,
  messages,
  typingUsers,
  _currentUser,
  // sendMessage,
  // handleTyping,
  markAsRead,
  loadMoreMessages,
  source = "",
  handleSendMessage,
  handleInputTyping,
}) => {
  console.log(user);
  console.log(messages);
  const userOnlineStatus = useSelector((state) => state.chat2.userOnlineStatus);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const lastMessageCountRef = useRef(0);
  const isUserScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const loadingTimeoutRef = useRef(null);

  const userId = localStorage.getItem("loggedInUser");
  const threadId = user?.threadId || user?.id;
  const activeCallId = useSelector((state) => state.calls?.activeByThread[threadId]);
  const inCallHere = useSelector(
    (state) =>
      Boolean(state.calls) &&
      !isIdle(state.calls.session.phase) &&
      String(state.calls?.session.threadId) === String(threadId)
  );
  const callPadding = !callsEnabled
    ? "5px"
    : activeCallId || user?.active_call || inCallHere
      ? "100px"
      : "36px";
  const threadMessages = messages[threadId] || { items: [], hasMore: true, loading: false };
  console.log(threadMessages);
  const chatMessages = (threadMessages.items || []).filter(isRenderableChatMessage);
  console.log(chatMessages);
  const typingUsersList = Object.keys(typingUsers[threadId] || {}).filter(
    (userId2) => userId2 !== userId
  );
  const typingUsersList2 = Object.values(typingUsers[threadId] || {});

  // Initialize chat when thread changes - immediately scroll to bottom like Messenger
  useEffect(() => {
    setHasInitialized(false);
    setShouldScrollToBottom(true);
    setShowScrollButton(false);
    lastMessageCountRef.current = chatMessages.length;
    isUserScrollingRef.current = false;
    setIsLoadingMore(false);

    // Clear any existing timeouts
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);

    // Immediately scroll to bottom when opening a chat (like Messenger)
    scrollTimeoutRef.current = setTimeout(() => {
      scrollToBottom(false); // Instant scroll for initialization
      setHasInitialized(true);
    }, 10);

    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    };
  }, [threadId]);

  // Handle new messages (real-time or sent by user)
  useEffect(() => {
    if (!hasInitialized) return;

    const newMessageCount = chatMessages.length;
    const hasNewMessages = newMessageCount > lastMessageCountRef.current;

    if (hasNewMessages) {
      if (shouldScrollToBottom && !isUserScrollingRef.current) {
        // Auto-scroll to new messages if user is at bottom
        setTimeout(() => scrollToBottom(true));
      } else if (!shouldScrollToBottom) {
        // Show scroll button for new messages when user is not at bottom
        setShowScrollButton(true);
      }
    }

    lastMessageCountRef.current = newMessageCount;
  }, [chatMessages.length, shouldScrollToBottom, hasInitialized]);

  // Mark messages as read
  useEffect(() => {
    if (!threadId || !chatMessages.length) return;
    console.log("Chat Messages", chatMessages);
    const unreadMessages = chatMessages
      .filter((msg) => !msg.read && msg?.sender?.id !== Number(userId))
      .map((msg) => msg.id)
      .filter((id) => id && !id.startsWith("temp_"));
    console.log("Chat Unread Messages", unreadMessages);
    if (unreadMessages.length > 0) {
      // This will now use WebSocket mark_read command when possible
      markAsRead(threadId, unreadMessages);
    }
  }, [threadId, chatMessages, markAsRead, userId]);

  const scrollToBottom = (smooth = true) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: smooth ? "smooth" : "auto",
        block: "end",
      });
      setShouldScrollToBottom(true);
      setShowScrollButton(false);
    }
  };

  const handleScroll = useCallback(async () => {
    const container = messagesContainerRef.current;
    if (!container || !hasInitialized) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    console.log(scrollHeight);
    console.log(scrollTop);
    console.log(clientHeight);
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 200;
    // const isAtTop = scrollTop < 50;

    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Set user scrolling flag
    isUserScrollingRef.current = true;
    scrollTimeoutRef.current = setTimeout(() => {
      isUserScrollingRef.current = false;
    }, 150);

    setShouldScrollToBottom(isAtBottom);
    setShowScrollButton(!isAtBottom && chatMessages.length > 0);

    // Load more messages when at top (silent loading like Messenger)
    // if (isAtTop && !isLoadingMore && threadMessages.hasMore && !threadMessages.loading) {
    //   setIsLoadingMore(true);

    //   const previousScrollHeight = scrollHeight;
    //   const previousScrollTop = scrollTop;

    //   // Start loading timeout to reset loading state if it takes too long
    //   loadingTimeoutRef.current = setTimeout(() => {
    //     setIsLoadingMore(false);
    //   }, 10000); // 10 second timeout

    //   try {
    //     await loadMoreMessages(threadId);
    //     setTimeout(() => {
    //       if (messagesContainerRef.current) {
    //         const newScrollHeight = messagesContainerRef.current.scrollHeight;
    //         const scrollOffset = newScrollHeight - previousScrollHeight;
    //         messagesContainerRef.current.scrollTop = previousScrollTop + scrollOffset;
    //       }
    //     }, 50);
    //   } catch (error) {
    //     console.error("Error loading more messages:", error);
    //   } finally {
    //     if (loadingTimeoutRef.current) {
    //       clearTimeout(loadingTimeoutRef.current);
    //     }
    //     setIsLoadingMore(false);
    //   }
    // }
  }, [
    threadId,
    threadMessages.hasMore,
    threadMessages.loading,
    isLoadingMore,
    loadMoreMessages,
    chatMessages.length,
    hasInitialized,
  ]);

  const handleLoadMoreClick = async () => {
    if (!isLoadingMore && threadMessages.hasMore && !threadMessages.loading) {
      setIsLoadingMore(true);
      try {
        await loadMoreMessages(threadId);
      } catch (error) {
        console.error("Error loading more messages:", error);
      } finally {
        setIsLoadingMore(false);
      }
    }
  };
  console.log(user);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
        backgroundColor: "white",
      }}
    >
      {/* Chat Header */}
      <div
        className="chat-window-header"
        style={{
          position: "relative",
          // borderBottom: "1px solid #e5e7eb",
          backgroundColor: "var(--primary-5)",
          flexShrink: 0,
          // Equal side padding keeps the centred title clear of the call button.
          paddingLeft: callPadding,
          paddingRight: callsEnabled ? callPadding : undefined,
        }}
      >
        <CallHeaderActions thread={user} />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: user?.scope === "CASE_PARTNER" ? "center" : "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "14px" }}>
            {/* <div style={{ position: "relative" }}>
              <div
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "50%",
                  backgroundColor: "var(--primary, #3b82f6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontWeight: "600",
                  fontSize: "16px",
                }}
              >
                {user?.name[0]?.toUpperCase() || "?"}
              </div>
              <span
                style={{
                  position: "absolute",
                  width: "12px",
                  height: "12px",
                  backgroundColor: "#10b981",
                  bottom: "2px",
                  right: "2px",
                  borderRadius: "50%",
                  border: "2px solid white",
                }}
              />
            </div> */}
            <div style={{ minWidth: 0, maxWidth: "100%" }}>
              <div
                className="chat-window-title"
                style={{
                  margin: "0 0 0px 0",
                  fontWeight: "600",
                  // color: "#1f2937",
                  color: "var(--primary)",
                  fontSize: "14px",
                  display: "flex",
                  gap: "5px",
                  minHeight: "25px",
                  flexWrap: "wrap",
                }}
              >
                {user?.scope === "CASE_PARTNER" ? (
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        minHeight: "25px",
                        minWidth: 0,
                        maxWidth: "100%",
                        color: "var(--primary)",
                      }}
                    >
                      <img
                        src={user?.client_avatar_url || avatarImg}
                        alt={user?.client_name}
                        width={15}
                        height={15}
                        style={{
                          width: "15px",
                          height: "15px",
                          flexShrink: 0,
                          borderRadius: "50%",
                          objectFit: "contain",
                          background: "white",
                        }}
                      />
                      <span
                        style={{
                          fontSize: "14px",
                        }}
                      >
                        {user?.client_name}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        minHeight: "25px",
                        minWidth: 0,
                        maxWidth: "100%",
                        color: "var(--primary)",
                      }}
                    >
                      {user?.case_type_icon && (
                        <img
                          src={user?.case_type_icon}
                          alt={user?.case_type_name}
                          width={15}
                          height={15}
                          style={{
                            width: "15px",
                            height: "15px",
                            flexShrink: 0,
                            borderRadius: "0%",
                            objectFit: "contain",
                          }}
                        />
                      )}
                      <span
                        style={{
                          fontSize: "14px",
                        }}
                      >
                        {user?.case_type_name}
                      </span>{" "}
                      Chat
                    </div>
                  </>
                ) : user?.thread_type === "group" ? (
                  <>
                    <span>{formatName(user?.name)} Chat</span>
                  </>
                ) : user?.name ? (
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "5px",
                        height: "25px",
                        color: "var(--primary)",

                        fontSize: "14px",
                      }}
                    >
                      <UserChatBubble2
                        first_name={user?.first_name ?? ""}
                        last_name={user?.last_name ?? ""}
                        profilePic={user?.client_avatar ?? ""}
                        width={21}
                        hideIcon={true}
                        isActive={userOnlineStatus?.[user?.other_participant_id]?.isOnline ?? false}
                      />{" "}
                      Chat
                    </div>
                  </>
                ) : user?.case_display ? (
                  `Chat about ${user.case_display}`
                ) : (
                  "Unknown User"
                )}
              </div>
            </div>
            {user?.thread_type === "group" && (
              // Full firm user chat buttons when they all fit on the line; otherwise this avatar stack.
              <GroupMembers group={user} title={`${formatName(user?.name)} Chat`}>
                <div
                  style={{
                    // paddingLeft: "5px",
                    // backgroundColor: "var(--primary-10)",
                    flexShrink: 0,
                    minWidth: "0", // Allow shrinking
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden", // Hide overflow
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "14px",
                        minWidth: "0",
                        flex: 1,
                      }}
                    >
                      <div>
                        <div
                          className="avatar-container"
                          style={{
                            margin: "0",
                            fontWeight: "600",
                            color: "#1f2937",
                            fontSize: "14px",
                            display: "flex",
                            height: "25px",
                            alignItems: "center",
                            position: "relative",
                            minWidth: "0",
                            overflow: "hidden",
                          }}
                        >
                          {user?.group_member_avatars?.slice(0, 5).map((u, index) => {
                            const isActive = userOnlineStatus?.[u?.id]?.isOnline || false;
                            const totalAvatars = user?.group_member_avatars?.length || 0;
                            const isLastVisible = index === 4 && totalAvatars > 5;

                            return (
                              <div
                                key={index}
                                className="avatar-item"
                                style={{
                                  width: `${19}px`,
                                  height: `${19}px`,
                                  borderRadius: "50%",
                                  overflow: "hidden",
                                  position: "relative",
                                  flexShrink: 1,
                                  marginLeft: index > 0 ? "var(--avatar-spacing, 3px)" : "0",
                                  zIndex: 5 - index,
                                }}
                              >
                                {!isLastVisible ? (
                                  <>
                                    <img
                                      src={u?.avatar_url || avatarImg}
                                      style={{
                                        width: `${18}px`,
                                        height: `${18}px`,
                                        objectFit: "cover",
                                        borderRadius: "50%",
                                      }}
                                      className="d-flex align-items-center justify-content-center"
                                      alt="Profile"
                                    />
                                    <div
                                      style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: `${19}px`,
                                        height: `${19}px`,
                                        borderRadius: "50%",
                                        border: isActive
                                          ? "2.5px solid #009900"
                                          : "2.5px solid var(--primary-20)",
                                        pointerEvents: "none",
                                        zIndex: "1",
                                      }}
                                    />
                                  </>
                                ) : (
                                  <div
                                    style={{
                                      width: `${19}px`,
                                      height: `${19}px`,
                                      borderRadius: "50%",
                                      backgroundColor: "#e5e7eb",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: "9px",
                                      fontWeight: "600",
                                      color: "#374151",
                                      border: "2.5px solid var(--primary-20)",
                                    }}
                                  >
                                    +{totalAvatars - 4}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </GroupMembers>
            )}
          </div>
        </div>
      </div>

      {/* Messages Container */}
      <div
        ref={messagesContainerRef}
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflowY: "auto",
          backgroundColor: "#f8fafc", //f8fafc
          // borderLeft: "1px solid var(--primary-25)",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          scrollbarWidth: "none",
        }}
        onScroll={handleScroll}
      >
        {/* Load More Messages Button - Shows at top when hasMore is true */}
        {threadMessages.hasMore && threadMessages.oldestMessageId && chatMessages.length > 0 && (
          <div style={{ display: "flex", justifyContent: "center", padding: "16px" }}>
            <button
              onClick={handleLoadMoreClick}
              disabled={isLoadingMore || threadMessages.loading}
              style={{
                padding: "8px 16px",
                backgroundColor:
                  isLoadingMore || threadMessages.loading ? "#f3f4f6" : "var(--primary, #3b82f6)",
                color: isLoadingMore || threadMessages.loading ? "#6b7280" : "white",
                border: "none",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "600",
                cursor: isLoadingMore || threadMessages.loading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s ease",
                // boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              {isLoadingMore || threadMessages.loading ? (
                <>
                  <div
                    style={{
                      width: "12px",
                      height: "12px",
                      border: "2px solid #d1d5db",
                      borderTop: "2px solid #6b7280",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                  Loading...
                </>
              ) : (
                <>↑ Load More Messages</>
              )}
            </button>
          </div>
        )}

        {/* Silent loading indicator at top - only shows when actually loading via scroll */}
        {isLoadingMore && threadMessages.hasMore && chatMessages.length > 0 && (
          <div style={{ display: "flex", justifyContent: "center", padding: "8px" }}>
            <div
              style={{
                width: "20px",
                height: "20px",
                border: "2px solid #f3f3f3",
                borderTop: "2px solid #3b82f6",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
          </div>
        )}

        {/* Messages */}
        <div style={{ padding: "5px", flex: 1, paddingRight: "15px", paddingLeft: "5px" }}>
          {chatMessages.length === 0 && !threadMessages.loading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              No messages yet. Start the conversation!
            </div>
          ) : (
            <MessageList messages={chatMessages} currentUserId={userId} />
          )}

          <TypingIndicator
            userIds={typingUsersList}
            typingUsersList2={typingUsersList2}
            user={user}
          />
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Scroll to bottom button */}
      {showScrollButton && source !== "chat" && (
        <button
          style={{
            position: "absolute",
            bottom: "80px",
            right: "24px",
            backgroundColor: "var(--primary, #3b82f6)",
            color: "white",
            border: "none",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
            transition: "all 0.3s ease",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 5,
          }}
          onClick={() => scrollToBottom(true)}
          title="Scroll to bottom"
          onMouseEnter={(e) => {
            e.target.style.transform = "scale(1.1)";
            e.target.style.backgroundColor = "#2563eb";
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = "scale(1)";
            e.target.style.backgroundColor = "var(--primary, #3b82f6)";
          }}
        >
          <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
            <path
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
              fill="none"
            />
          </svg>
        </button>
      )}

      {/* Message Input */}

      {source === "chat" && (
        <MessageInput
          onSendMessage={handleSendMessage}
          onTyping={handleInputTyping}
          placeholder={`Message ${user.name || user.case_display || "user"}...`}
          disabled={!threadId}
        />
      )}

      <style jsx>{`
        @keyframes typing-bounce {
          0%,
          80%,
          100% {
            transform: scale(0);
          }
          40% {
            transform: scale(1);
          }
        }
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default ChatWindow;
